import * as React from "react"
import { RoleId, ROLES, isCompanyAdmin, hasAccess, COST_MANAGEMENT_ACCESS } from "@/lib/roles"
import type { CompanyModuleMap } from "@/lib/company-modules"

/**
 * User data structure from Yii2 backend
 * This is passed via window.__USER__ from Yii2 view
 */
export type SiteSource = "travelhub" | "travelcode" | "bubadrom"
export type DateTimeFormatType = "europe" | "usa"
/** Mirrors common\models\user\Balance\Enums\WalletBalanceSource. */
export type WalletSource = "company" | "cards" | "hidden"

export interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  roleId: RoleId
  companyId: number
  avatar?: string
  source?: SiteSource
  /** Backend-computed: whether this brand collects Cyrillic names. */
  cyrillicNames?: boolean
  currency?: string
  lang?: string
  originalUserId?: number | null
  companyName?: string | null
  /** Absolute CDN url of the current company's logo, shown next to ours in the expanded sidebar. */
  companyLogo?: string | null
  /** Miles & Points pilot access (per-user checkbox in the profile); false → preview page. */
  milesPointsAccess?: boolean
  /** Company allows personal money at all — gates the «personal trip» toggle in the sidebar. */
  personalTripAllowed?: boolean
  /** Whether «личная поездка» mode is currently on for this user. */
  personalTripMode?: boolean
  /**
   * Personal is the company's only mode — no corporate/crew sales context, so there is no business
   * trip to go back to. Hides the sidebar switch and the banner's way out.
   */
  personalTripOnly?: boolean
  isOwner?: boolean
  /** Account owner (no ownerId), even with zero employees — role matrix keys off this. */
  isAccountOwner?: boolean
  /** Director without a company profile yet — shows the company-details banner */
  needsCompanyDetails?: boolean
  /**
   * A new client company has not finished the getting-started guide (UserAccess). The guide then
   * carries the company-details step and outranks the What's-new popup.
   */
  onboardingActive?: boolean
  /** An invited employee whose travel profile is not ready yet — the first-login welcome (UserAccess). */
  travellerWelcomeActive?: boolean
  /**
   * Whether an agreement is waiting for signature. Served with the page rather
   * than fetched: the banner renders over Yii2 pages too, so it cannot ask for
   * itself. Mirrors common\services\Legal\LegalAccountStatusService.
   */
  legal?: {
    requiresAction: boolean
    overdue: boolean
    count: number
    /** Unix seconds; the soonest 30-day window still running. */
    nearestDeadline: number | null
    restrictedProducts: string[]
    accountRestricted: boolean
  }
  needsTravelProfile?: boolean
  /** Login is delegated to Keycloak (keycloakOidcGate on) — enables passkey UI */
  keycloakEnabled?: boolean
  /** User already registered a passkey — hides the "set up a passkey" nudge */
  hasPasskey?: boolean
  /** Payment-profiles feature available (prod: owner ∈ {1,2626}; dev: everyone). Mirrors the backend gate. */
  paymentProfilesEnabled?: boolean
  /** Stripe plan subscriptions are live (subscriptionsGate) — gates the Plan & billing nav entry. */
  subscriptionsEnabled?: boolean
  /** Company allows employees to pay with personal cards. When false, employees see a restriction banner. */
  allowPersonalProfiles?: boolean
  cdnUrl?: string
  accessToken?: string
  canAdvancedSearch?: boolean
  hasExtendedFlightInfo?: boolean
  hasAirbnbBooking?: boolean
  /**
   * Airlines this storefront sells, when it is restricted to some. Sent resolved
   * (code + title) because the airline picker shows names. Empty for an
   * unrestricted storefront, which is nearly all of them.
   */
  flightCarriers?: { id: number; code: string; title: string }[]
  planCode?: "starter" | "premium" | "pro"
  dateTimeFormat?: DateTimeFormatType
  dateFormat?: string
  userType?: number
  /** Per-employee "may create travel profiles on the fly" flag — gates the
   *  standalone Travel-profiles page for corp non-management roles. */
  canCreateTravelProfiles?: boolean
  /** Company deposit, served only when walletSource is "company" — null otherwise. */
  balance?: number | null
  balanceCurrency?: string
  /**
   * Which balance this user's role may see, set per role by the company owner
   * (BalanceVisibilityPolicy). "company" = the deposit, "cards" = headroom on the
   * user's own Pliant cards, "hidden" = no Wallet at all.
   */
  walletSource?: WalletSource
  isAgent?: boolean
  defaultHotelSort?: "popular" | "price"
  defaultFlightSort?: "price" | "fast" | "optimal"
  /** Traveler nationality from the profile (ISO 3166-1 alpha-2), if set. */
  nationality?: string | null
  showDiscountedPrices?: boolean
  agentText?: string | null
  showChannelBadges?: boolean
  /**
   * Whether the user can use the Pliant virtual-cards feature (Cards /
   * Cardholders pages and the issue-card flow). Mirrors
   * PliantOrganizationService::canUse — admins/devs always, plus client users
   * carrying the SETTING_PLIANT_CARD_ACCESS flag.
   */
  canUsePliantCards?: boolean
  /**
   * False only for a guest traveller whose company turned guest booking off
   * (GuestTravellerCompanyAccessService::canBook) — no booking entry points.
   */
  canBook?: boolean
  /**
   * Whether this user may use expenses and the expense policy. Mirrors
   * ExpenseAccessService::canUse — admins/devs by role, plus users carrying
   * SETTING_EXPENSES_ACCESS. Independent of `canUsePliantCards` in both
   * directions: books and cards are separate products.
   */
  canUseExpenses?: boolean
  /**
   * Whether this user may use company budgets. Mirrors
   * BudgetAccessService::canUse — admins/devs by role, plus users carrying
   * SETTING_BUDGETS_ACCESS. Independent of `canUsePliantCards`: an envelope of
   * money needs no card, and a card needs no envelope.
   */
  canUseBudgets?: boolean
  /**
   * Whether this user may use trip cards. Mirrors TripCardAccessService::canUse
   * — staff by role, everybody else through the OWNER's bit or through the
   * company having its own Pliant organization.
   *
   * Granted on the COMPANY rather than per seat, which is the difference from
   * the three flags above: a trip card is carried by a traveller for the days
   * of a trip, so an employee is answered about their company.
   */
  canUseTripCards?: boolean
  /**
   * Whether auto-issue is open to this company at all. Mirrors
   * TripCardAutoIssuePolicy::mayAutoIssue — false for a company issued on our
   * system organization, where nobody would be signing for the money.
   *
   * Not a permission: it asks whose money a card would spend, which is why the
   * control is explained rather than hidden.
   */
  canAutoIssueTripCards?: boolean
  /** Heads a department in the pay scheme today (EarningsConfigService::isHeadOn). */
  isDepartmentHead?: boolean
  /** Has reports whose trips come to them for approval (policy option "the manager approves"). */
  approvesAsManager?: boolean
  /** Named approver in the company's travel policy or price-band rules (UserAccess::approvesByPolicy). */
  approvesByPolicy?: boolean
  /**
   * Whether this client runs on the embedded Travel Code card programme.
   * Mirrors PliantOrganizationService::isEmbedded.
   *
   * Not a permission like the three above — those say what the user may open,
   * this says what the provider will accept from their organization. The
   * embedded products carry their own card configs, transaction-count defaults
   * and the right to open or close a card account through the API; offering an
   * action the provider refuses is how a page comes to lie.
   */
  isPliantEmbedded?: boolean
  /**
   * Whether this role may open, rename and close a card account. Mirrors
   * CardAccountAccessPolicy::canManage — the role half only.
   *
   * The other half — whether the provider accepts account management from this
   * organization's programme — stays on the server and answers at call time, so
   * a caller outside it sees the form and a clear refusal rather than nothing at
   * all. Deliberate for the first iteration; the server tightens it when we know
   * which clients the provider serves.
   */
  canManageCardAccounts?: boolean
  /**
   * Product modules the company turned on («Company modules» page). Missing map
   * (older cached HTML) reads as "everything on" — see isModuleEnabled.
   */
  modules?: CompanyModuleMap
}

interface UserContextValue {
  user: User | null
  isAdmin: boolean
  isLoading: boolean
  isSwitchedUser: boolean
  dateTimeFormat: DateTimeFormatType
  dateFormat: string
  hasAccess: (module: keyof typeof COST_MANAGEMENT_ACCESS, action: string) => boolean
  /** For dev/mock: switch role */
  setMockRole: (role: "admin" | "user") => void
  /**
   * Update the passkey flag at runtime. `hasPasskey` is otherwise set once at
   * login; the Passkeys page calls this from its live list so the "set up a
   * passkey" banner reflects reality immediately (hide when ≥1, show when 0).
   */
  setHasPasskey: (has: boolean) => void
  /**
   * Replace the module map at runtime. The «Company modules» page calls it after
   * a save so the sidebar reflects the new set without a page reload — the map
   * otherwise only arrives with the document.
   */
  setModules: (modules: CompanyModuleMap) => void
  /**
   * Replace the current company's logo at runtime, so the sidebar header
   * updates right after an upload on /app/companies instead of waiting for
   * the next document.
   */
  setCompanyLogo: (companyLogo: string | null) => void
  /**
   * Clear (or raise) the company-details nudge at runtime. The flag is a
   * page-load snapshot; /app/companies calls this after the director creates
   * their first company so the top banner goes away without a reload.
   */
  setNeedsCompanyDetails: (needs: boolean) => void
  /**
   * Replace the name, email or avatar at runtime, so the sidebar shows what
   * /app/profile-general just saved instead of waiting for the next document.
   */
  setProfileIdentity: (patch: Partial<Pick<User, "firstName" | "lastName" | "email" | "avatar">>) => void
}

const UserContext = React.createContext<UserContextValue | undefined>(undefined)

/**
 * Declare the global window.__USER__ from Yii2
 */
declare global {
  interface Window {
    __USER__?: User
    __FLIGHT_SERVICES__?: {
      labels?: Record<string, string>
      negotiatedRate: string[]
      sirena: string[]
      channels?: Record<string, string>
    }
  }
}

/**
 * Mock users for development/testing
 */
const MOCK_USERS: Record<"admin" | "user", User> = {
  admin: {
    id: 1,
    email: "admin@company.com",
    firstName: "Admin",
    lastName: "User",
    roleId: ROLES.ADMIN,
    companyId: 1,
  },
  user: {
    id: 2,
    email: "employee@company.com",
    firstName: "John",
    lastName: "Employee",
    roleId: ROLES.EMPLOYEE,
    companyId: 1,
  },
}

/** Validate that window.__USER__ has all required fields before using it */
function isValidUser(data: unknown): data is User {
  if (typeof data !== 'object' || data === null) return false
  const u = data as Record<string, unknown>
  return (
    typeof u.id === 'number' &&
    typeof u.email === 'string' &&
    typeof u.roleId === 'number' &&
    typeof u.companyId === 'number'
  )
}

interface UserProviderProps {
  children: React.ReactNode
  /** Override user for testing/storybook */
  testUser?: User
  /** Enable mock mode for development */
  enableMock?: boolean
  /** Initial mock role */
  initialMockRole?: "admin" | "user"
}

export function UserProvider({
  children,
  testUser,
  enableMock = true,
  initialMockRole = "admin",
}: UserProviderProps) {
  const [user, setUser] = React.useState<User | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [mockRole, setMockRole] = React.useState<"admin" | "user">(initialMockRole)

  React.useEffect(() => {
    // Use test user if provided (for development/testing)
    if (testUser) {
      setUser(testUser)
      setIsLoading(false)
      return
    }

    // Get user from Yii2 via window.__USER__
    if (typeof window !== 'undefined' && window.__USER__) {
      if (isValidUser(window.__USER__)) {
        setUser(window.__USER__)
      }
      setIsLoading(false)
      return
    }

    // Use mock user in development
    if (enableMock) {
      setUser(MOCK_USERS[mockRole])
    }
    setIsLoading(false)
  }, [testUser, enableMock, mockRole])

  // Handle mock role change
  const handleSetMockRole = React.useCallback((role: "admin" | "user") => {
    setMockRole(role)
    if (enableMock && !window.__USER__) {
      setUser(MOCK_USERS[role])
    }
  }, [enableMock])

  const setHasPasskey = React.useCallback((has: boolean) => {
    setUser((prev) => (prev && prev.hasPasskey !== has ? { ...prev, hasPasskey: has } : prev))
  }, [])

  const setModules = React.useCallback((modules: CompanyModuleMap) => {
    setUser((prev) => (prev ? { ...prev, modules } : prev))
    // Keep the source of truth in sync too: a Yii2 page navigated to right after
    // a save re-reads window.__USER__, not this state.
    if (typeof window !== "undefined" && window.__USER__) {
      window.__USER__.modules = modules
    }
  }, [])

  const setCompanyLogo = React.useCallback((companyLogo: string | null) => {
    setUser((prev) => (prev ? { ...prev, companyLogo } : prev))
    // Same reason as setModules: a Yii2 page opened right after the save reads
    // window.__USER__, not this state.
    if (typeof window !== "undefined" && window.__USER__) {
      window.__USER__.companyLogo = companyLogo
    }
  }, [])

  const setNeedsCompanyDetails = React.useCallback((needs: boolean) => {
    setUser((prev) => (prev && prev.needsCompanyDetails !== needs ? { ...prev, needsCompanyDetails: needs } : prev))
    // Same reason as setModules: a Yii2 page opened right after the save reads
    // window.__USER__, not this state.
    if (typeof window !== "undefined" && window.__USER__) {
      window.__USER__.needsCompanyDetails = needs
    }
  }, [])

  const setProfileIdentity = React.useCallback(
    (patch: Partial<Pick<User, "firstName" | "lastName" | "email" | "avatar">>) => {
      setUser((prev) => (prev ? { ...prev, ...patch } : prev))
      // Same reason as setModules: a Yii2 page opened right after the save reads
      // window.__USER__, not this state.
      if (typeof window !== "undefined" && window.__USER__) {
        Object.assign(window.__USER__, patch)
      }
    },
    []
  )

  const isAdmin = user ? isCompanyAdmin(user.roleId) : false
  const isSwitchedUser = !!user?.originalUserId
  const dateTimeFormat: DateTimeFormatType = user?.dateTimeFormat === "usa" ? "usa" : "europe"
  const dateFormat = user?.dateFormat || "dd.MM.yyyy"

  const checkAccess = React.useCallback(
    (module: keyof typeof COST_MANAGEMENT_ACCESS, action: string) => {
      if (!user) return false
      return hasAccess(user.roleId, module, action)
    },
    [user]
  )

  const value: UserContextValue = {
    user,
    isAdmin,
    isLoading,
    isSwitchedUser,
    dateTimeFormat,
    dateFormat,
    hasAccess: checkAccess,
    setMockRole: handleSetMockRole,
    setHasPasskey,
    setModules,
    setCompanyLogo,
    setNeedsCompanyDetails,
    setProfileIdentity,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

/**
 * Hook to access current user data
 */
export function useUser(): UserContextValue {
  const context = React.useContext(UserContext)
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider")
  }
  return context
}

/**
 * Hook to check if current user is company admin
 */
export function useIsAdmin(): boolean {
  const { isAdmin } = useUser()
  return isAdmin
}

/**
 * Hook to check access to specific feature
 */
export function useAccess(module: keyof typeof COST_MANAGEMENT_ACCESS, action: string): boolean {
  const { hasAccess: checkAccess } = useUser()
  return checkAccess(module, action)
}

/**
 * Hook to get date/time format preferences
 */
export function useDateTimeFormat(): { dateTimeFormat: DateTimeFormatType; dateFormat: string } {
  const { dateTimeFormat, dateFormat } = useUser()
  return { dateTimeFormat, dateFormat }
}
